import { fullDisplayName } from "src/util/personName";

export type ManagerRef = {
  firstName?: string | null;
  surname?: string | null;
  preferredName?: string | null;
};

export type PersonWithHierarchies = {
  managedByHierarchy?: (ManagerRef | null)[] | null;
  supplyManagedByHierarchy?: (ManagerRef | null)[] | null;
};

const ELLIPSIS = "…";
const DEFAULT_MAX_BREADCRUMB_MANAGERS = 3;

/**
 * Manager names, root first, for display. The API orders the two chains
 * differently: `managedByHierarchy` is root first, `supplyManagedByHierarchy`
 * is nearest first. Either may hold null entries (disabled people, unowned
 * supply levels). Prefers the supply chain when team owners are on, falling
 * back to the HRIS chain when the person has no supply chain (e.g. not on an
 * owned team).
 */
export const getReportingLineNames = (
  person: PersonWithHierarchies | null | undefined,
  useSupplyChain: boolean
): string[] => {
  const present = (chain?: (ManagerRef | null)[] | null) =>
    (chain ?? []).filter((manager): manager is ManagerRef => Boolean(manager));

  const supply = useSupplyChain
    ? present(person?.supplyManagedByHierarchy).reverse()
    : [];
  const chain = supply.length ? supply : present(person?.managedByHierarchy);
  return chain.map(fullDisplayName);
};

/**
 * The names to show for a root-first chain, truncated from the top so the
 * direct manager is always last; a leading "…" marks what was cut.
 */
export const truncateReportingLine = (
  namesRootFirst: string[],
  maxManagers = DEFAULT_MAX_BREADCRUMB_MANAGERS
): string[] =>
  namesRootFirst.length <= maxManagers
    ? namesRootFirst
    : [ELLIPSIS, ...namesRootFirst.slice(-maxManagers)];
